/**
 * Prevents GTM from loading on payment pages.
 *
 * The @zadigetvoltaire/nuxt-gtm module registers a Nuxt plugin that injects
 * the GTM <script> tag during app boot — before route middleware runs.
 * Running this plugin with enforce: 'pre' ensures it executes first and sets
 * gtm.enabled = false in runtimeConfig before the GTM plugin reads it,
 * which prevents the GTM script from ever being added to the DOM on these pages.
 *
 * The router middleware (gtm-payment-guard.global.ts) handles the complementary
 * case: re-enabling GTM if the user navigates away from a payment page.
 */

import { PAYMENT_STEP } from '~/constants/application/health-insurance-v2'

const PAYMENT_ROUTE_PREFIXES = [
  '/health-insurance/application/payment',
  `/health-insurance/applicationV2/${PAYMENT_STEP}`
] as const

export default defineNuxtPlugin({
  name: 'gtm-payment-block',
  enforce: 'pre',
  setup () {
    // Read the path from window.location rather than useRoute(): on a direct
    // hydration load, the Vue Router instance may still be on its placeholder
    // route ('/') when 'pre' plugins run, so route.path would not match the
    // payment prefixes and GTM would slip through. window.location.pathname
    // is always the real URL the browser navigated to.
    const pathname = window.location.pathname
    if (PAYMENT_ROUTE_PREFIXES.some(prefix => pathname.startsWith(prefix))) {
      const runtimeConfig = useRuntimeConfig()
      // Setting both flags is belt-and-braces: vue-gtm only injects the
      // <script> when (enabled && loadScript) is true, so disabling either
      // is sufficient — but a downstream plugin elsewhere in the app could
      // toggle one back on, and we want this guard to remain authoritative.
      runtimeConfig.public.gtm.enabled = false
      runtimeConfig.public.gtm.loadScript = false
    }
  }
})
